import * as THREE from 'three';

export type Vector3Tuple = [number, number, number];

export interface CameraAdaptiveObbOptions {
  cameraPosition: THREE.Vector3;
  cameraTarget: THREE.Vector3;
  up?: THREE.Vector3;
  bounds: THREE.Box3 | null;
}

export interface CameraAdaptiveObbParameters {
  origin: Vector3Tuple;
  axisX: Vector3Tuple;
  axisY: Vector3Tuple;
  axisZ: Vector3Tuple;
  min: Vector3Tuple;
  max: Vector3Tuple;
  direction: Vector3Tuple;
  sweepStart: number;
  sweepEnd: number;
}

const DEFAULT_FORWARD = new THREE.Vector3(0, 1, 0);
const DEFAULT_UP = new THREE.Vector3(0, 0, 1);

export function createCameraAdaptiveObb(options: CameraAdaptiveObbOptions): CameraAdaptiveObbParameters {
  const up = normalizeOrFallback(options.up, DEFAULT_UP);
  const forward = computeHorizontalForward(options.cameraPosition, options.cameraTarget, up);
  const axisY = forward;
  const axisX = normalizeOrFallback(new THREE.Vector3().crossVectors(axisY, up), new THREE.Vector3(1, 0, 0));
  const axisZ = normalizeOrFallback(new THREE.Vector3().crossVectors(axisX, axisY), up);
  const origin = options.cameraPosition.clone();
  const localBounds = projectBoundsToObb(options.bounds, origin, axisX, axisY, axisZ);

  const minY = 0;
  const maxY = Math.max(0.001, localBounds.max.y);
  const min = new THREE.Vector3(localBounds.min.x, minY, localBounds.min.z);
  const max = new THREE.Vector3(localBounds.max.x, maxY, localBounds.max.z);
  const direction = new THREE.Vector3(0, 1, 0);
  const sweepStart = Math.max(0, Math.min(maxY, localBounds.min.y));
  const sweepEnd = Math.max(sweepStart + 0.001, Math.min(maxY, localBounds.max.y));

  return {
    origin: toTuple(origin),
    axisX: toTuple(axisX),
    axisY: toTuple(axisY),
    axisZ: toTuple(axisZ),
    min: toTuple(min),
    max: toTuple(max),
    direction: toTuple(direction),
    sweepStart,
    sweepEnd,
  };
}

function computeHorizontalForward(cameraPosition: THREE.Vector3, cameraTarget: THREE.Vector3, up: THREE.Vector3): THREE.Vector3 {
  const forward = cameraTarget.clone().sub(cameraPosition);
  forward.addScaledVector(up, -forward.dot(up));
  return normalizeOrFallback(forward, DEFAULT_FORWARD);
}

function normalizeOrFallback(value: THREE.Vector3 | undefined, fallback: THREE.Vector3): THREE.Vector3 {
  if (!value || value.lengthSq() < 1e-8) {
    return fallback.clone().normalize();
  }
  return value.clone().normalize();
}

function projectBoundsToObb(
  bounds: THREE.Box3 | null,
  origin: THREE.Vector3,
  axisX: THREE.Vector3,
  axisY: THREE.Vector3,
  axisZ: THREE.Vector3
): THREE.Box3 {
  if (!bounds || bounds.isEmpty()) {
    return new THREE.Box3(new THREE.Vector3(-0.5, 0, -0.5), new THREE.Vector3(0.5, 1, 0.5));
  }

  const localBounds = new THREE.Box3();
  for (const corner of getBoxCorners(bounds)) {
    const delta = corner.sub(origin);
    localBounds.expandByPoint(new THREE.Vector3(delta.dot(axisX), delta.dot(axisY), delta.dot(axisZ)));
  }
  return localBounds;
}

function getBoxCorners(box: THREE.Box3): THREE.Vector3[] {
  const { min, max } = box;
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
}

function toTuple(value: THREE.Vector3): Vector3Tuple {
  return [sanitizeZero(value.x), sanitizeZero(value.y), sanitizeZero(value.z)];
}

function sanitizeZero(value: number): number {
  return Math.abs(value) < 1e-12 ? 0 : value;
}
